import { Hit } from "instantsearch.js";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  InstantSearchServerState,
  useInstantSearch,
} from "react-instantsearch-core-v7";
import { DeprecatedAlgoliaSearchProduct } from "src/types/Algolia";
import { CATEGORIES_FACET, CATEGORY_PAGE_ID_FACET } from "src/utils/algolia";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { useRefineCategoryPageId } from "./useRefineCategoryPageId";
import { PLPTypes } from "src/types/PLPType.d";
import { BaseHit } from "instantsearch.js/es/types";
import { PLPListTypes } from "src/utils/algolia/contextKeys";
import {
  queryStringReverseKeyMap,
  queryStringValuePosReverseMap,
  useHandleSearchParams,
  useRemoveFilters,
} from "src/hooks/use-handle-search-params";
import { SEGMENT_LIST_FORMAT } from "src/segment";
import { LimitType, useSegmentProductList } from "../useSegmentProductList";
import {
  CatalogProduct,
  HomepageSingleCategoryProduct,
} from "src/types/graphql.d";
import { useRouter } from "next/router";
import { safeJsonParse } from "src/utils/helper/json-parser";
import { useCustomInfiniteHits } from "./use-custom-infinite-hits";
import { orderByValueMap } from "src/organisms/ProductList/Facets/Sort";

type ProductListState = {
  isLoading: boolean;
  products: Hit<DeprecatedAlgoliaSearchProduct & BaseHit>[];
  total: number;
  hasNoResults: boolean;
};

export type UseProductListingProps = {
  title?: string;
  gender: string;
  categoryPageId?: string;
  collectionId?: string;
  hitsPerPage: number;
  pageType: PLPTypes;
  indexName: string;
  serverState?: InstantSearchServerState;
  gridRef?: MutableRefObject<HTMLDivElement | null>;
};

export type UseProductListingResult = ProductListState & {
  nextPage: number;
  previousPage: number;
  cacheSize: number;
  showPreviousButton: boolean;
  isFirstPage: boolean;
  isLastPage: boolean;
  showMoreProducts: () => void;
  showPreviousProducts: () => void;
  segmentProductOnClick: (
    product: (CatalogProduct | HomepageSingleCategoryProduct) & {
      objectID?: string;
    },
    position: number
  ) => void;
  setVisibleProducts: (
    product: CatalogProduct | HomepageSingleCategoryProduct,
    position: number,
    limitType?: LimitType
  ) => void;
};

function useProductListing({
  gender,
  categoryPageId,
  pageType,
  indexName,
  title,
  serverState,
  gridRef,
}: UseProductListingProps): UseProductListingResult {
  const categoryPageIdRef = useRef<string>(categoryPageId || "");
  const nbHitsRef = useRef(0);
  const isFirstRenderRef = useRef(true);
  const initialFiltersRef = useRef(false);
  const showMoreRef = useRef(false);

  const router = useRouter();

  const [listingState, setListingState] = useState<ProductListState>({
    isLoading: true,
    products: isFirstRenderRef.current
      ? (serverState?.initialResults?.[indexName]?.results?.[0]?.hits as Hit<
          DeprecatedAlgoliaSearchProduct & BaseHit
        >[]) || []
      : [],
    total: 0,
    hasNoResults: false,
  });

  const { results, status, setIndexUiState } = useInstantSearch();
  const {
    items,
    nextPage,
    previousPage,
    showMore,
    loading,
    cacheSize,
    showPreviousButton,
    isFirstPage,
    isLastPage,
    nbPages,
  } = useCustomInfiniteHits<DeprecatedAlgoliaSearchProduct>();
  const { handleSearchParams } = useHandleSearchParams();
  const { removeAllFiltersByKey } = useRemoveFilters();

  const { segmentProductOnClick, setVisibleProducts } = useSegmentProductList({
    algoliaIndex: indexName,
    listFormat: SEGMENT_LIST_FORMAT.default,
    listId: title || "",
    listType: PLPListTypes[pageType],
    queryID: results.queryID,
    numberOfItems: results.nbHits,
    shopType: localeFix({ gender }),
  });

  const isLoadingComplete = useMemo(() => {
    const isLoading =
      !results.__isArtificial &&
      nbHitsRef.current !== results.nbHits &&
      status === "idle";

    nbHitsRef.current = results.nbHits;

    return isLoading;
  }, [results.__isArtificial, results.nbHits, status]);

  const categories = useMemo(() => {
    const allServerResults =
      pageType !== "SEARCH_PLP"
        ? serverState?.initialResults?.[indexName]?.results?.find((item) =>
            item.facets ? item.facets[CATEGORIES_FACET] : {}
          )?.facets || {}
        : {};

    const items: object =
      allServerResults[CATEGORIES_FACET] ||
      results.disjunctiveFacets.find(({ name }) => name === CATEGORIES_FACET)
        ?.data ||
      {};

    return items ? Object.keys(items) : [];
  }, [
    results.disjunctiveFacets,
    serverState?.initialResults,
    indexName,
    pageType,
  ]);

  const initialFacets = useMemo(() => {
    const allFacets = serverState?.initialResults?.[indexName]?.results
      ?.flat()
      .reduce((result, item) => {
        if (item.facets) {
          Object.entries(item.facets).forEach(([key, value]) => {
            result[key] = result[key]
              ? [...result[key], ...Object.keys(value)]
              : Object.keys(value);
          });
        }

        return result;
      }, {} as Record<string, string[]>);

    return allFacets;
  }, [serverState?.initialResults, indexName]);

  const hasNoResults = useMemo(() => {
    if (isFirstRenderRef.current) {
      return false;
    }

    const url = new URL(location.toString());
    const currentPage = Number(url.searchParams.get("page")) || 1;

    return (
      (!results.__isArtificial &&
        results.nbHits === 0 &&
        !isFirstRenderRef.current &&
        status === "idle") ||
      (nbPages > 0 && nbPages < currentPage)
    );
  }, [results.__isArtificial, results.nbHits, status, nbPages]);

  const total = useMemo(() => results.nbHits, [results.nbHits]);

  useRefineCategoryPageId({
    items: categories,
    mainCategoryPageId: categoryPageId || "",
    setIndexUiState,
    isLoadingComplete,
  });

  const clear = useCallback(() => {
    setIndexUiState((prevState) => ({
      ...prevState,
      page: 0,
      refinementList: {},
    }));
  }, [setIndexUiState]);

  const handleScrollTo = useCallback(() => {
    const id = Buffer.from(router.asPath).toString("base64");
    const savedScrollPos = sessionStorage.getItem("ot_scroll_position");
    const parsedSavedScrollPos = savedScrollPos
      ? safeJsonParse<Record<string, string>>(savedScrollPos)
      : {};

    if (parsedSavedScrollPos && parsedSavedScrollPos[id]) {
      window.scrollTo({
        top: Number(parsedSavedScrollPos[id]),
      });

      delete parsedSavedScrollPos[id];
      sessionStorage.setItem(
        "ot_scroll_position",
        JSON.stringify(parsedSavedScrollPos)
      );
    }
  }, [router]);

  const handleScrollPosition = useCallback(
    (url: string) => {
      if (!url.includes("/product/")) {
        if ("scrollRestoration" in history) {
          // Reset to auto if not product page
          history.scrollRestoration = "auto";
        }

        return;
      }

      const id = Buffer.from(router.asPath).toString("base64");

      const updateSavedScrollPos = {
        [id]: window.scrollY,
      };

      sessionStorage.setItem(
        "ot_scroll_position",
        JSON.stringify(updateSavedScrollPos)
      );

      if ("scrollRestoration" in history) {
        // Force manual scroll for product pages
        history.scrollRestoration = "manual";
      }
    },
    [router.asPath]
  );

  const handleInitialRefinements = useCallback(() => {
    if (initialFiltersRef.current || isFirstRenderRef.current) {
      return;
    }

    const url = new URL(location.toString());
    const orderByDefaultValue = url.searchParams.get("orderBy");

    const initialRefinements: Record<string, string[]> = {};

    for (const [key, value] of url.searchParams.entries()) {
      let facetKey = queryStringReverseKeyMap[key];
      let facetValue = initialFacets?.[facetKey]?.find((item) => {
        const splitVal = item.split("||");

        return splitVal.at(queryStringValuePosReverseMap[key]) === value;
      });

      if (facetKey === CATEGORIES_FACET) {
        const facetValueSplit = facetValue?.split("||");

        facetValue = facetValueSplit?.at(-1);
        facetKey = CATEGORY_PAGE_ID_FACET;
      }

      if (facetValue) {
        initialRefinements[facetKey] = initialRefinements[facetKey]
          ? [...initialRefinements[facetKey], facetValue]
          : [facetValue];
      }
    }

    // Remove parent categories from initial refinements
    if (initialRefinements?.[CATEGORY_PAGE_ID_FACET]) {
      const parentCategoryIds: string[] = [];

      // As we don't have a direct mapping of parent-child relationships in the facets,
      // we need to loop through the initial refinements to find which keys are parents.
      for (const key of initialRefinements?.[CATEGORY_PAGE_ID_FACET] || []) {
        for (const subKey of initialRefinements?.[CATEGORY_PAGE_ID_FACET] ||
          []) {
          // If the subkey starts with the key it means that key is a parent category
          // so we add it to the parentCategoryIds array and break the loop, to avoid unnecessary checks.
          // We also ensure that we don't add the key itself as a parent.
          if (subKey.startsWith(key) && subKey !== key) {
            parentCategoryIds.push(key);
            break;
          }
        }
      }

      // Only filter out parent categories if there are any
      if (parentCategoryIds.length > 0) {
        initialRefinements[CATEGORY_PAGE_ID_FACET] = initialRefinements?.[
          CATEGORY_PAGE_ID_FACET
        ]?.filter((item) => !parentCategoryIds.includes(item));
      }
    }

    setIndexUiState((prevState) => {
      return {
        ...prevState,
        sortBy:
          orderByDefaultValue &&
          !indexName.includes(orderByValueMap[orderByDefaultValue])
            ? `${indexName}_${orderByValueMap[orderByDefaultValue]}`
            : indexName,
        refinementList: {
          ...prevState.refinementList,
          ...initialRefinements,
        },
        page: Number(url.searchParams.get("page")) || 0,
      };
    });

    setListingState((prevState) => ({
      ...prevState,
      products: [],
    }));

    initialFiltersRef.current = true;
  }, [indexName, initialFacets, setIndexUiState]);

  const handleRouteUpdate = useCallback(() => {
    sessionStorage.removeItem("ot_scroll_position");

    const url = new URL(location.toString());
    const orderByDefaultValue = url.searchParams.get("orderBy");

    const initialRefinements: Record<string, string[]> = {};

    for (const [key, value] of url.searchParams.entries()) {
      let facetKey = queryStringReverseKeyMap[key];
      let facetValue = initialFacets?.[facetKey]?.find((item) => {
        const splitVal = item.split("||");

        return splitVal.at(queryStringValuePosReverseMap[key]) === value;
      });

      if (facetKey === CATEGORIES_FACET) {
        const facetValueSplit = facetValue?.split("||");

        facetValue = facetValueSplit?.at(-1);
        facetKey = CATEGORY_PAGE_ID_FACET;
      }

      if (facetValue) {
        initialRefinements[facetKey] = initialRefinements[facetKey]
          ? [...initialRefinements[facetKey], facetValue]
          : [facetValue];
      }
    }

    // Remove parent categories from initial refinements
    if (initialRefinements?.[CATEGORY_PAGE_ID_FACET]) {
      const parentCategoryIds: string[] = [];

      // As we don't have a direct mapping of parent-child relationships in the facets,
      // we need to loop through the initial refinements to find which keys are parents.
      for (const key of initialRefinements?.[CATEGORY_PAGE_ID_FACET] || []) {
        for (const subKey of initialRefinements?.[CATEGORY_PAGE_ID_FACET] ||
          []) {
          // If the subkey starts with the key it means that key is a parent category
          // so we add it to the parentCategoryIds array and break the loop, to avoid unnecessary checks.
          // We also ensure that we don't add the key itself as a parent.
          if (subKey.startsWith(key) && subKey !== key) {
            parentCategoryIds.push(key);
            break;
          }
        }
      }

      // Only filter out parent categories if there are any
      if (parentCategoryIds.length > 0) {
        initialRefinements[CATEGORY_PAGE_ID_FACET] = initialRefinements?.[
          CATEGORY_PAGE_ID_FACET
        ]?.filter((item) => !parentCategoryIds.includes(item));
      }
    }

    setIndexUiState((prevState) => {
      return {
        ...prevState,
        refinementList: {
          ...initialRefinements,
        },
        sortBy: orderByDefaultValue
          ? `${indexName}_${orderByValueMap[orderByDefaultValue]}`
          : indexName,
        page: Number(url.searchParams.get("page")) || 0,
      };
    });

    gridRef?.current?.scrollIntoView();
  }, [indexName, gridRef, initialFacets, setIndexUiState]);

  const customShowMore = useCallback(() => {
    showMoreRef.current = true;

    handleSearchParams({
      facet: "page",
      value: `${nextPage + 1}`,
      isSingle: true,
    });

    showMore();
  }, [nextPage, showMore, handleSearchParams]);

  const customShowPrev = useCallback(() => {
    if (previousPage - 1 > 1) {
      handleSearchParams({
        facet: "page",
        value: `${previousPage - 1}`,
        isSingle: true,
      });
    } else {
      removeAllFiltersByKey("page");
    }

    setIndexUiState((prevState) => {
      return {
        ...prevState,
        page: previousPage - 1,
      };
    });
  }, [
    previousPage,
    setIndexUiState,
    handleSearchParams,
    removeAllFiltersByKey,
  ]);

  useEffect(() => {
    if (categoryPageIdRef.current !== categoryPageId) {
      categoryPageIdRef.current = categoryPageId || "";
      initialFiltersRef.current = false;

      clear();
    }
  }, [categoryPageId, clear]);

  useEffect(() => {
    router.beforePopState(() => {
      handleRouteUpdate();

      return true;
    });

    router.events.on("routeChangeStart", handleScrollPosition);
    router.events.on("routeChangeComplete", handleInitialRefinements);

    return () => {
      router.events.off("routeChangeStart", handleScrollPosition);
      router.events.off("routeChangeComplete", handleInitialRefinements);
      router.beforePopState(() => true);
    };
  }, [
    router,
    handleRouteUpdate,
    handleScrollPosition,
    handleInitialRefinements,
  ]);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;

      return;
    }

    if (loading) {
      return;
    }

    if (categories.length > 0 && !initialFiltersRef.current) {
      handleInitialRefinements();

      return;
    }

    setListingState((prevState) => ({
      ...prevState,
      products: items,
      isLoading: loading,
      total,
      hasNoResults,
    }));

    showMoreRef.current = false;
  }, [
    status,
    items,
    loading,
    total,
    hasNoResults,
    categories,
    router.asPath,
    handleInitialRefinements,
  ]);

  useEffect(() => {
    if (
      isFirstRenderRef.current ||
      !initialFiltersRef.current ||
      listingState.isLoading ||
      listingState.products.length === 0
    ) {
      return;
    }

    handleScrollTo();
  }, [listingState.products.length, listingState.isLoading, handleScrollTo]);

  return {
    ...listingState,
    nextPage,
    previousPage,
    cacheSize,
    showPreviousButton,
    isFirstPage,
    isLastPage,
    showMoreProducts: customShowMore,
    showPreviousProducts: customShowPrev,
    segmentProductOnClick,
    setVisibleProducts,
  };
}

export { useProductListing };
